<template>
  <div>
    <Header />
    
    <!-- Enhanced Header Section -->
    <div class="enhanced-header">
    <v-container fluid>
        <div class="header-content">
          <div class="header-title-section">
            <h1 class="page-title">
              <v-icon color="white" class="mr-3">mdi-cart-variant</v-icon>
              Warenkorbregal Verwaltung
            </h1>
          </div>
          
          <div class="header-actions">
            <v-btn
              color="white darken-2"
              large
              @click="showExtraSlotsModal = true"
              class="action-button mr-3"
            >
              <v-icon left>mdi-inbox-multiple</v-icon>
              Sperrgut-Regale
            </v-btn>

            <v-btn
              color="white darken-2"
              large
              @click="showPrinterSelectionDialog = true"
              class="action-button mr-3"
            >
            <v-icon left>mdi-printer</v-icon>
              Drucker
          </v-btn>

            <v-btn
              :loading="loadingAllOrders"
              :disabled="loadingAllOrders"
              color="white darken-2"
              large
              @click="loadAllOrders"
              class="action-button"
            >
            <v-icon left>mdi-refresh</v-icon>
            Alle Daten laden
          </v-btn>
          </div>
        </div>

        <!-- Control Bar -->
        <div class="controls-bar">
          <div class="controls-flex">
            <div class="input-group">
              <v-btn-toggle v-model="shelfState" mandatory class="shelf-toggle" color="white">
                <v-btn value="bottom">
                  <v-icon left>mdi-arrow-down-bold</v-icon>
                  Unteres Regal
                </v-btn>
                <v-btn value="top">
                  <v-icon left>mdi-arrow-up-bold</v-icon>
                  Oberes Regal (O-W)
                </v-btn>
              </v-btn-toggle>
            </div>
            
            <div class="input-group">
              <v-text-field 
                v-model="searchQuery" 
                label="Suche in Artikelattributen" 
                prepend-inner-icon="mdi-magnify"
                outlined 
                dense
                clearable
                class="search-field"
                hide-details
              ></v-text-field>
            </div>
            
            <!-- Compact Order Status Overview -->
            <div v-if="shelfState === 'bottom'" class="order-status-overview">
              <div class="status-badge status-ready" @click="scrollToOrderSection('ready')">
                <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                <div class="status-info">
                  <div class="status-count">{{ readyOrders.length }}</div>
                  <div class="status-label">Bereit</div>
                </div>
              </div>
              
              <div class="status-badge status-ready-to-pack" @click="scrollToOrderSection('readyToPack')">
                <v-icon small class="mr-1">mdi-package-check</v-icon>
                <div class="status-info">
                  <div class="status-count">{{ readyToPackOrders.length }}</div>
                  <div class="status-label">Packbereit</div>
                </div>
              </div>
              
              <div class="status-badge status-incomplete" @click="scrollToOrderSection('incomplete')">
                <v-icon small class="mr-1">mdi-package-variant</v-icon>
                <div class="status-info">
                  <div class="status-count">{{ incompleteOrders.length }}</div>
                  <div class="status-label">Unvollständig</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </div>
    
    <v-container fluid>

      <!-- Fortschrittsbalken -->
      <v-row v-if="loadingAllOrders">
        <v-col cols="12">
          <v-progress-linear :value="loadProgress" :color="progressColor" height="25" striped>
            <template v-slot:default>
              <strong>{{ Math.ceil(loadProgress) }}% ({{ loadedOrdersCount }}/{{ totalOrders }})</strong>
            </template>
          </v-progress-linear>
        </v-col>
      </v-row>

      <!-- Unteres Regal Visualisierung -->
      <v-row v-if="shelfState === 'bottom'" class="shelf-container">
        <v-col cols="12" class="shelf-col">
          <div v-for="column in ['A', 'B', 'C', 'D', 'E']" :key="column" class="shelf-grid-row">
            <div class="shelf-column-header">{{ column }}</div>
            <div class="shelf-slots-container">
              <div
                v-for="slot in 20"
                :key="slot"
                :class="['shelf-slot', getSlotClass(getSlotContent(column + slot))]"
                @click="showSlotDetails(column + slot)"
              >
                <div v-if="isSlotLocked(getSlotContent(column + slot))" class="slot-lock-badge">
                  <v-icon color="white" x-small>mdi-lock</v-icon>
                </div>
                <div v-if="hasSlotProblem(getSlotContent(column + slot))" class="slot-problem-badge">
                  <v-icon color="white" x-small>mdi-alert</v-icon>
                </div>
                <div class="slot-id">{{ column }}{{ slot }}</div>
                <div v-if="getSlotContent(column + slot)" class="slot-info">
                  <div class="slot-count">{{ getSlotContent(column + slot).articles.length }}</div>
                  <div class="slot-age">{{ getSlotAgeShort(getSlotContent(column + slot)) }}</div>
                </div>
              </div>
            </div>
          </div>
                </v-col>
              </v-row>

      <!-- O-W-Regal Visualisierung (Top Shelf) -->
      <v-row v-if="shelfState === 'top'" class="shelf-container">
        <v-col cols="12" class="shelf-col">
          <div v-for="column in ['A', 'B', 'C', 'D', 'E']" :key="`ow-${column}`" class="shelf-grid-row">
            <div class="shelf-column-header">O-W-{{ column }}</div>
            <div class="shelf-slots-container">
              <div
                v-for="slot in 10"
                :key="slot"
                :class="['shelf-slot', getSlotClass(getSlotContent(`O-W-${column}${slot}`))]"
                @click="showSlotDetails(`O-W-${column}${slot}`)"
              >
                <div v-if="isSlotLocked(getSlotContent(`O-W-${column}${slot}`))" class="slot-lock-badge">
                  <v-icon color="white" x-small>mdi-lock</v-icon>
                </div>
                <div v-if="hasSlotProblem(getSlotContent(`O-W-${column}${slot}`))" class="slot-problem-badge">
                  <v-icon color="white" x-small>mdi-alert</v-icon>
                </div>
                <div class="slot-id">{{ slot }}</div>
                <div v-if="getSlotContent(`O-W-${column}${slot}`)" class="slot-info">
                  <div class="slot-count">{{ getSlotContent(`O-W-${column}${slot}`).articles.length }}</div>
                  <div class="slot-age">{{ getSlotAgeShort(getSlotContent(`O-W-${column}${slot}`)) }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Drucker-Auswahl Dialog -->
      <v-dialog v-model="showPrinterSelectionDialog" max-width="400px">
        <v-card>
          <v-card-title>Wähle Deinen Drucker aus</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="height: 300px">
            <v-radio-group v-model="printer" column>
              <v-radio v-for="p in printers" :key="p._id" :label="p.name" :value="p"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="savePrinter(); showPrinterSelectionDialog = false">
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>

    <!-- Problem Dialog -->
    <v-dialog v-model="showProblemDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Problem melden
        </v-card-title>
        
        <v-card-text class="pt-4">
          <p class="text-body-1 mb-4">
            Bitte wählen Sie den Problemtyp aus und beschreiben Sie das Problem genauer.
          </p>
          
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedProblemType"
                label="Art des Problems"
                :items="problemTypes"
                outlined
                dense
                required
              ></v-select>
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                v-model="problemDescription"
                label="Problembeschreibung"
                outlined
                rows="4"
                counter="500"
                :rules="[v => v.length <= 500 || 'Max 500 Zeichen']"
              ></v-textarea>
            </v-col>
          </v-row>
          
          <v-alert v-if="problemOrderId" type="info" class="mt-4">
            <strong>Bestellnummer:</strong> {{ problemOrderId }}
            <div v-if="orderArticles[problemOrderId]">
              <strong>Bestellalter:</strong> {{ getOrderAgeInHours(problemOrderId) }} Stunden
            </div>
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeProblemDialog">
            Abbrechen
          </v-btn>
          <v-btn 
            color="error" 
            @click="reportProblem"
            :loading="reportingProblem"
            :disabled="!selectedProblemType || !problemDescription"
          >
            Problem melden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailDialog" max-width="1000">
      <v-card v-if="selectedSlot" class="detail-dialog">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <v-icon class="mr-2">mdi-package-variant</v-icon>
            <span>Slot {{ selectedSlot.slot }}</span>
            <v-chip small class="ml-3">{{ selectedSlot.orders.length }} Bestellung(en)</v-chip>
          </div>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="dialog-content">
          <v-overlay :value="isLoading" absolute>
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>

          <div class="orders-grid">
            <v-card
              v-for="order in selectedSlot.orders"
              :key="order.orderId"
              class="order-card"
              :class="{ 'order-card-problem': order.hasProblem, 'order-card-old': isOrderOld(order) }"
              elevation="2"
            >
              <!-- Loading State -->
              <div v-if="loadingStates[order.orderId]" class="order-loading">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                <div class="mt-2">Lade Daten...</div>
              </div>

              <!-- Order Content -->
              <div v-else class="order-card-content">
                <!-- Header -->
                <div class="order-header">
                  <div class="order-id">
                    <v-icon class="mr-1">mdi-tag</v-icon>
                    {{ order.orderId }}
                  </div>
                  <v-chip
                    x-small
                    :color="getOrderReadyStatus(orderArticles[order.orderId]?.products).color"
                    dark
                  >
                      {{ getOrderReadyStatus(orderArticles[order.orderId]?.products).text }}
                    </v-chip>
                  </div>

                <!-- Info Grid -->
                <div class="order-info-grid">
                  <div class="info-item">
                    <v-icon small class="info-icon">mdi-clock-outline</v-icon>
                    <div class="info-content">
                      <div class="info-label">Alter</div>
                      <div class="info-value">{{ getArticleAge(order) }}</div>
                  </div>
                </div>

                  <div class="info-item">
                    <v-icon small class="info-icon">mdi-package-variant</v-icon>
                    <div class="info-content">
                      <div class="info-label">Artikel</div>
                      <div class="info-value">{{ order.articles.length }}</div>
                    </div>
                  </div>

                  <div class="info-item">
                    <v-icon small class="info-icon">mdi-truck</v-icon>
                    <div class="info-content">
                      <div class="info-label">Versand</div>
                      <div class="info-value">{{ orderArticles[order.orderId]?.shipping?.type || 'Standard' }}</div>
                    </div>
                  </div>

                  <div class="info-item">
                    <v-icon small class="info-icon">mdi-information</v-icon>
                    <div class="info-content">
                      <div class="info-label">Status</div>
                      <div class="info-value">
                        <v-chip x-small :color="getOrderStatusColor(orderArticles[order.orderId]?.state)">
                          {{ orderArticles[order.orderId]?.state || 'Unbekannt' }}
                        </v-chip>
                        </div>
                    </div>
                  </div>
                </div>

                <!-- Warnings/Badges -->
                <div v-if="order.hasProblem || isOrderOld(order)" class="order-badges">
                  <v-chip v-if="order.hasProblem" x-small color="error" dark>
                    <v-icon x-small left>mdi-alert</v-icon>
                    Problem gemeldet
                  </v-chip>
                  <v-chip v-if="isOrderOld(order)" x-small color="warning" dark>
                    <v-icon x-small left>mdi-clock-alert</v-icon>
                    Überfällig
                  </v-chip>
                </div>

                <!-- Actions -->
                <div class="order-actions">
                  <v-tooltip bottom :disabled="isOrderReadyToPack(order)">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" style="width: 100%;">
                        <v-btn
                          color="primary"
                          block
                          @click="openOrderInWizard(order.orderId)"
                          class="mb-2"
                          :disabled="!isOrderReadyToPack(order)"
                        >
                          <v-icon left small>mdi-package-variant</v-icon>
                          Im Wizard packen
                        </v-btn>
                      </div>
                    </template>
                    <span>{{ getPackDisabledReason(order) }}</span>
                  </v-tooltip>

                  <v-btn
                    color="info"
                    block
                    outlined
                    @click="openSupportPage(order.orderId)"
                    :disabled="!canReportProblem(order)"
                  >
                    <v-icon left small>mdi-help-circle</v-icon>
                    Zum Support
                  </v-btn>

                  <v-btn
                    v-if="order.hasProblem"
                    color="success"
                    block
                    outlined
                    @click="unreportProblem(order.orderId)"
                    class="mt-2"
                  >
                    <v-icon left small>mdi-check-circle</v-icon>
                    Problem lösen
                  </v-btn>
                </div>
                              </div>
                </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog für unvollständige Bestellungen -->
    <v-dialog v-model="showConfirmationDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Achtung: Unvollständige Bestellung</v-card-title>
        <v-card-text>
          Diese Bestellung ist nicht vollständig. Möchtest du sie trotzdem drucken und packen?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="showConfirmationDialog = false">Abbrechen</v-btn>
          <v-btn color="success" @click="confirmPrintAndPack">Bestätigen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Extra Slots Management Modal -->
    <extra-slots-modal
      v-model="showExtraSlotsModal"
      :printer="printer"
      @success="handleExtraSlotSuccess"
      @error="handleExtraSlotError"
    />
  </div>
</template>

<script>
import Header from '@/views/private/header';
import moment from 'moment';
import PacklistService from '@/services/PacklistService';
import AuthService from '@/services/AuthService';
import ExtraSlotsModal from './modals/ExtraSlotsModal.vue';
import orderCacheDB from '@/utils/OrderCacheDB';

export default {
  name: 'MultiCartShelfManager',
  components: {
    Header,
    ExtraSlotsModal
  },
  data: () => ({
    shelfState: 'bottom', // 'bottom' or 'top'
    slots: {},
    detailDialog: false,
    selectedSlot: null,
    refreshInterval: null,
    orderArticles: {},
    isLoading: false,
    loadingStates: {},
    loadedOrders: {},
    loadingAllOrders: false,
    loadProgress: 0,
    totalOrders: 0,
    loadedOrdersCount: 0,
    showPrinterSelectionDialog: false,
    printers: null,
    printer: null,
    printing: false,
    searchQuery: '',
    searchResults: new Set(),
    showConfirmationDialog: false,
    pendingPrintData: null,
    showProblemDialog: false,
    selectedProblemType: null,
    problemDescription: '',
    problemOrderId: null,
    reportingProblem: false,
    problemTypes: [
      'Fehlende Produkte',
      'Beschädigte Produkte',
      'Falsche Produkte',
      'Druckfehler',
      'Systemfehler',
      'Sonstiges'
    ],
    showExtraSlotsModal: false,
  }),

  async created() {
    // Prüfe ob User Admin ist
    const user = (await AuthService.getUser()).data;
    if (!user || (user.role !== 'admin' && user.role !== 'worker')) {
      this.$router.push('/');
      return;
    }

    // Initialize IndexedDB
    await orderCacheDB.init();

    // Clean up legacy localStorage caches (one-time migration)
    this.cleanupLegacyLocalStorageCache();

    // Clean up expired caches on startup
    await this.cleanupExpiredOrderCache();

    await this.loadData();

    // Auto-load cached orders on startup (instant, no API calls)
    await this.loadCachedOrders();

    // Refresh data every 30 seconds and clean up expired caches
    this.refreshInterval = setInterval(async () => {
      this.loadData();
      await this.cleanupExpiredOrderCache();
    }, 30000);

    await this.loadPrinters();
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    async loadData() {
      try {
        const response = await PacklistService.getMultiCartShelfStatus();
        this.slots = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der Daten:', error);
      }
    },

    getSlotContent(slotId) {
      return this.slots[slotId] || null;
    },

    searchInSlot(slot, query) {
      if (!slot || !slot.articles || !query) return false;

      for (const article of slot.articles) {
        const orderDetails = this.orderArticles[article.orderId];
        if (!orderDetails?.products) continue;

        for (const product of orderDetails.products) {
          if (!product.attributes) continue;

          // Durchsuche alle Attribute
          for (const [key, value] of Object.entries(product.attributes)) {
            if (value && value.toString().toLowerCase().includes(query.toLowerCase())) {
              return true;
            }
          }
        }
      }
      return false;
    },

    getSlotClass(slot) {
      if (!slot || !slot.articles || slot.articles.length === 0) return 'empty-slot';

      const slotId = slot.articles[0]?.shelf?.slot;
      // Wenn es ein Suchergebnis ist
      if (this.searchQuery && this.searchInSlot(slot, this.searchQuery)) {
        return 'search-result-slot';
      }

      // Rest der bestehenden Logik
      const orderGroups = {};
      slot.articles.forEach(article => {
        if (!orderGroups[article.orderId]) {
          orderGroups[article.orderId] = {
            articles: [],
            orderArticles: this.orderArticles[article.orderId]?.products || [],
            hasProblem: article.hasProblem || false
          };
        }
        orderGroups[article.orderId].articles.push(article);
      });

      // Prüfe ob irgendeine Bestellung ein Problem hat
      const hasProblem = Object.values(orderGroups).some(group => group.hasProblem);
      if (hasProblem) return 'problem-slot';

      const hasCompleteOrder = Object.values(orderGroups).some(group => {
        // Filtere Artikel, die berücksichtigt werden müssen (mit Attributen außer nur vQty)
        const relevantArticles = group.orderArticles.filter(article => this.shouldConsiderForShelfStatus(article));

        // Wenn es keine relevanten Artikel gibt, ist die Bestellung "vollständig"
        if (relevantArticles.length === 0) return false;

        // Prüfe, ob alle relevanten Artikel im Regal sind
        const allRelevantArticlesInShelf = relevantArticles.every(article => article.shelf);
        return allRelevantArticlesInShelf && relevantArticles.length > 0;
      });

      if (hasCompleteOrder) return 'complete-slot';

      const oldestArticle = this.getOldestArticle(slot);
      if (!oldestArticle) return 'empty-slot';

      // Versuche createDate zu verwenden
      let oldestDate = null;
      
      slot.articles.forEach(article => {
        const orderDetails = this.orderArticles[article.orderId];
        if (orderDetails && orderDetails.createDate) {
          const createDate = moment(orderDetails.createDate);
          if (!oldestDate || createDate.isBefore(oldestDate)) {
            oldestDate = createDate;
          }
        }
      });
      
      // Fallback auf timestamp wenn kein createDate gefunden wurde
      if (!oldestDate) {
        oldestDate = moment(oldestArticle.timestamp);
      }
      
      const hours = moment().diff(oldestDate, 'hours');

      if (hours > 36) return 'critical-slot';
      if (hours > 24) return 'warning-slot';
      return 'active-slot';
    },

    getOldestArticle(slot) {
      if (!slot || !slot.articles || slot.articles.length === 0) return null;
      return slot.articles.reduce((oldest, current) => {
        return moment(current.timestamp).isBefore(moment(oldest.timestamp)) ? current : oldest;
      }, slot.articles[0]);
    },

    getSlotAge(slot) {
      if (!slot || !slot.articles || slot.articles.length === 0) return '';
      
      // Finde den ältesten createDate aus den orderArticles
      let oldestCreateDate = null;
      
      slot.articles.forEach(article => {
        const orderDetails = this.orderArticles[article.orderId];
        if (orderDetails && orderDetails.createDate) {
          const createDate = moment(orderDetails.createDate);
          if (!oldestCreateDate || createDate.isBefore(oldestCreateDate)) {
            oldestCreateDate = createDate;
          }
        }
      });
      
      // Fallback auf timestamp wenn kein createDate gefunden wurde
      if (!oldestCreateDate) {
        const oldestArticle = this.getOldestArticle(slot);
        return moment(oldestArticle.timestamp).fromNow();
      }
      
      return oldestCreateDate.fromNow();
    },

    getSlotAgeShort(slot) {
      if (!slot || !slot.articles || slot.articles.length === 0) return '';
      
      // Finde den ältesten createDate aus den orderArticles
      let oldestCreateDate = null;
      
      slot.articles.forEach(article => {
        const orderDetails = this.orderArticles[article.orderId];
        if (orderDetails && orderDetails.createDate) {
          const createDate = moment(orderDetails.createDate);
          if (!oldestCreateDate || createDate.isBefore(oldestCreateDate)) {
            oldestCreateDate = createDate;
          }
        }
      });
      
      // Fallback auf timestamp wenn kein createDate gefunden wurde
      if (!oldestCreateDate) {
        const oldestArticle = this.getOldestArticle(slot);
        oldestCreateDate = moment(oldestArticle.timestamp);
      }
      
      const hours = moment().diff(oldestCreateDate, 'hours');
      if (hours < 1) return '< 1h';
      if (hours < 24) return `${hours}h`;
      const days = Math.floor(hours / 24);
      return `${days}d`;
    },

    getStatusColor(state) {
      const colors = {
        'pending': 'grey',
        'in_progress': 'blue',
        'completed': 'success',
        'error': 'error',
        'waiting': 'orange'
      };
      return colors[state] || 'grey';
    },

    async loadOrderArticles(orderId) {
      try {
        // Check in-memory cache first
        if (this.orderArticles[orderId]) {
          return; // Already loaded in memory
        }

        // Check IndexedDB cache second
        const cached = await orderCacheDB.getOrder(orderId);
        if (cached) {
          this.$set(this.orderArticles, orderId, cached);
          console.log(`📦 Loaded order ${orderId} from IndexedDB cache`);
          return;
        }

        // Load from API as last resort
        const response = await PacklistService.getPacklistItem(orderId);
        if (response.data) {
          this.$set(this.orderArticles, orderId, response.data);
          // Cache for future use in IndexedDB
          await orderCacheDB.saveOrder(orderId, response.data);
          console.log(`📦 Loaded order ${orderId} from API and cached in IndexedDB`);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Auftragsdaten:', error);
      }
    },

    async loadOrderData(orderId) {
      // Skip if already loaded in memory
      if (this.orderArticles[orderId]) {
        this.$set(this.loadedOrders, orderId, true);
        return;
      }

      this.$set(this.loadingStates, orderId, true);
      try {
        await this.loadOrderArticles(orderId);
        this.$set(this.loadedOrders, orderId, true);
      } catch (error) {
        console.error('Fehler beim Laden der Bestelldaten:', error);
        this.$set(this.loadedOrders, orderId, false);
      } finally {
        this.$set(this.loadingStates, orderId, false);
      }
    },

    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm:ss');
    },

    getArticleAge(order) {
      // Verwende createDate aus orderArticles statt timestamp
      const orderDetails = this.orderArticles[order.orderId];
      if (orderDetails && orderDetails.createDate) {
        return moment(orderDetails.createDate).fromNow();
      }
      // Fallback auf timestamp wenn kein createDate vorhanden
      return moment(order.timestamp).fromNow();
    },

    isOrderOld(order) {
      // Verwende createDate aus orderArticles statt timestamp
      const orderDetails = this.orderArticles[order.orderId];
      if (orderDetails && orderDetails.createDate) {
        const hours = moment().diff(moment(orderDetails.createDate), 'hours');
        return hours > 36;
      }
      // Fallback auf timestamp wenn kein createDate vorhanden
      const hours = moment().diff(moment(order.timestamp), 'hours');
      return hours > 36;
    },

    async removeArticle(hashcode) {
      try {
        await PacklistService.removeMultiCartArticle(hashcode);
        await this.loadData();
        if (this.selectedSlot) {
          this.selectedSlot.articles = this.selectedSlot.articles.filter(
            a => a.hashcode !== hashcode
          );
        }
      } catch (error) {
        console.error('Fehler beim Entfernen des Artikels:', error);
      }
    },

    getOrderStatusColor(state) {
      const colors = {
        'UNSHIPPED': 'warning',
        'SHIPPED': 'success',
        'CANCELLED': 'error',
        'PENDING': 'grey',
        'IN_PROGRESS': 'info'
      };
      return colors[state] || 'grey';
    },

    hasCustomText(item) {
      return item.translated?.line1 && !item.translated.line1.includes('customily.com');
    },

    getCustomText(item) {
      if (!item.translated) return '';
      const texts = [];
      for (let i = 1; i <= 5; i++) {
        if (item.translated[`line${i}`] && !item.translated[`line${i}`].includes('customily.com')) {
          texts.push(item.translated[`line${i}`]);
        }
      }
      return texts.join(' ');
    },

    getProductName(item) {
      if (!item?.sku) return '';

      // Entfernt technische Präfixe und formatiert den SKU-Namen
      const name = item.sku
        .replace(/^LYTO_/, '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
      return name;
    },

    findArticlePosition(item) {
      for (const [slotId, slotContent] of Object.entries(this.slots)) {
        const foundArticle = slotContent.articles?.find(a =>
          a.orderId === item.orderId && a.sku === item.sku
        );
        if (foundArticle) return slotId;
      }
      return null;
    },

    getLocationStatusColor(position) {
      return position ? 'success' : 'grey';
    },

    getLocationStatus(position) {
      return position ? `Slot ${position}` : 'Ausstehend';
    },

    getArticleStatus(item) {
      // Wenn der Artikel einen Shelf-Status hat
      if (item.shelf) {
        return {
          text: `Im Regal (${item.shelf.slot})`,
          color: 'success'
        };
      }

      // Status basierend auf dem item.status
      switch (item.status) {
        case 'ready':
          return { text: 'Bereit', color: 'info' };
        case 'missing':
          return { text: 'Fehlend', color: 'error' };
        default:
          return { text: 'Ausstehend', color: 'warning' };
      }
    },

    async showSlotDetails(slotId) {
      this.loadingStates = {};
      this.loadedOrders = {};

      const slotContent = this.getSlotContent(slotId);

      // Gruppiere Artikel nach OrderId
      const groupedArticles = {};
      slotContent?.articles.forEach(article => {
        if (!groupedArticles[article.orderId]) {
          groupedArticles[article.orderId] = {
            orderId: article.orderId,
            articles: [],
            timestamp: article.timestamp,
            hasProblem: article.hasProblem || false
          };
        }
        groupedArticles[article.orderId].articles.push(article);
      });

      // Sortiere nach Timestamp (älteste zuerst)
      const sortedOrders = Object.values(groupedArticles).sort((a, b) =>
        moment(a.timestamp).diff(moment(b.timestamp))
      );

      this.selectedSlot = {
        slot: slotId,
        orders: sortedOrders
      };

      this.detailDialog = true;

      // Lade Details für jede Order
      sortedOrders.forEach(order => {
        if (!this.orderArticles[order.orderId]) {
          this.loadOrderData(order.orderId);
        } else {
          this.$set(this.loadedOrders, order.orderId, true);
        }
      });
    },

    getOrderReadyStatus(articles) {
      if (!articles || articles.length === 0) return { text: 'Keine Artikel', color: 'grey' };

      // Filtere relevante Artikel (mit Attributen außer nur vQty)
      const relevantArticles = articles.filter(article => this.shouldConsiderForShelfStatus(article));

      // Wenn keine relevanten Artikel vorhanden sind
      if (relevantArticles.length === 0) return { text: 'Nur Zubehör', color: 'success' };

      const allReady = relevantArticles.every(article => article.status === 'ready');
      const allInShelf = relevantArticles.every(article => article.shelf);
      const anyMissing = relevantArticles.some(article => article.status === 'missing');

      if (allInShelf) return { text: 'Alles im Regal', color: 'success' };
      if (allReady) return { text: 'Alle bereit', color: 'info' };
      if (anyMissing) return { text: 'Fehlende Artikel', color: 'error' };
      return { text: 'In Bearbeitung', color: 'warning' };
    },

    // IndexedDB Cache Methods
    async clearAllOrderCache() {
      await orderCacheDB.clearAll();
    },

    cleanupLegacyLocalStorageCache() {
      // Clean up old localStorage caches (migration to IndexedDB)
      const keys = Object.keys(localStorage);
      let removedCount = 0;

      keys.forEach(key => {
        if (key.startsWith('order_') || key === 'orderCacheMetadata' || key === 'multiCartShelfBulkCache') {
          localStorage.removeItem(key);
          removedCount++;
        }
      });

      if (removedCount > 0) {
        console.log(`🧹 Cleaned up ${removedCount} legacy localStorage cache entries (migrated to IndexedDB)`);
      }
    },

    async cleanupExpiredOrderCache() {
      await orderCacheDB.cleanupExpiredOrders();
    },

    async loadCachedOrders() {
      try {
        // Check if we have a complete dataset cached in IndexedDB
        const bulkCache = await orderCacheDB.getBulkCache();

        if (bulkCache && bulkCache.orderArticles) {
          console.log(`🚀 Loading complete dataset from IndexedDB bulk cache...`);

          // Restore all order data instantly
          Object.keys(bulkCache.orderArticles).forEach(orderId => {
            this.$set(this.orderArticles, orderId, bulkCache.orderArticles[orderId]);
            this.$set(this.loadedOrders, orderId, true);
          });

          console.log(`✅ Loaded ${Object.keys(bulkCache.orderArticles).length} orders from IndexedDB (0 API calls)`);

          // Force UI update
          this.$nextTick(() => {
            this.$forceUpdate();
          });

          return; // Done - bulk cache loaded successfully
        }

        console.log('📦 No bulk cache found in IndexedDB');

      } catch (error) {
        console.error('Error loading cached orders from IndexedDB:', error);
      }
    },

    async loadAllOrders() {
      this.loadingAllOrders = true;
      this.loadProgress = 0;
      this.loadedOrdersCount = 0;

      // Sammle alle einzigartigen OrderIds
      const orderIds = new Set();
      Object.values(this.slots).forEach(slot => {
        slot.articles.forEach(article => {
          orderIds.add(article.orderId);
        });
      });

      this.totalOrders = orderIds.size;

      console.log(`📦 Loading ${this.totalOrders} orders (with IndexedDB cache)`);

      // Lade alle Orders parallel mit max 5 gleichzeitig
      const chunks = Array.from(orderIds).reduce((acc, curr, i) => {
        const chunkIndex = Math.floor(i / 5);
        if (!acc[chunkIndex]) acc[chunkIndex] = [];
        acc[chunkIndex].push(curr);
        return acc;
      }, []);

      let cacheHits = 0;
      let apiCalls = 0;

      for (const chunk of chunks) {
        await Promise.all(chunk.map(async orderId => {
          try {
            // Check in-memory cache first
            if (this.orderArticles[orderId]) {
              this.$set(this.loadedOrders, orderId, true);
              cacheHits++;
            } else {
              // Check IndexedDB cache second
              const cached = await orderCacheDB.getOrder(orderId);
              if (cached) {
                this.$set(this.orderArticles, orderId, cached);
                this.$set(this.loadedOrders, orderId, true);
                cacheHits++;
              } else {
                // Load from API and cache in IndexedDB
                const response = await PacklistService.getPacklistItem(orderId);
                if (response.data) {
                  this.$set(this.orderArticles, orderId, response.data);
                  this.$set(this.loadedOrders, orderId, true);
                  await orderCacheDB.saveOrder(orderId, response.data);
                  apiCalls++;
                }
              }
            }
          } catch (error) {
            console.error(`Fehler beim Laden der Order ${orderId}:`, error);
            this.$set(this.loadedOrders, orderId, false);
          }
          this.loadedOrdersCount++;
          this.loadProgress = (this.loadedOrdersCount / this.totalOrders) * 100;
        }));
      }

      this.loadingAllOrders = false;
      console.log(`✅ Loaded ${this.totalOrders} orders (${cacheHits} from cache, ${apiCalls} API calls)`);

      // Save complete dataset to IndexedDB bulk cache for instant reload
      try {
        console.log(`💾 Saving bulk cache to IndexedDB...`);
        await orderCacheDB.saveBulkCache(this.orderArticles);
        console.log(`✅ Bulk cache saved successfully to IndexedDB`);
      } catch (error) {
        console.error('Error saving bulk cache to IndexedDB:', error);
      }
    },

    async loadPrinters() {
      try {
        const response = await PacklistService.getPrinters();
        this.printers = response.data;
        const currentPrinter = this.$cookies.get("printer");
        if (currentPrinter && currentPrinter._id) {
          this.printer = this.printers.find(p => p._id === currentPrinter._id);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Drucker:', error);
      }
    },

    savePrinter() {
      this.$cookies.set("printer", this.printer);
    },

    async printAndPack(slotId, specificOrderId = null) {
      const printer = this.$cookies.get("printer");
      if (!printer || !printer.url) {
        this.showPrinterSelectionDialog = true;
        return;
      }

      // Prüfen, ob die Bestellung vollständig ist
      if (specificOrderId && !this.isOrderComplete(this.orderArticles[specificOrderId]?.products)) {
        // Bestellung ist unvollständig, speichere die Parameter und zeige Bestätigungsdialog
        this.pendingPrintData = { slotId, specificOrderId };
        this.showConfirmationDialog = true;
        return;
      }

      // Bestellung ist vollständig oder wurde bestätigt, führe den Druck durch
      await this.executePrintAndPack(slotId, specificOrderId);
    },

    async confirmPrintAndPack() {
      this.showConfirmationDialog = false;
      if (this.pendingPrintData) {
        await this.executePrintAndPack(this.pendingPrintData.slotId, this.pendingPrintData.specificOrderId);
        this.pendingPrintData = null;
      }
    },

    async executePrintAndPack(slotId, specificOrderId = null) {
      try {

        const printer = this.$cookies.get("printer");
        if (!printer || !printer.url) {
          this.showPrinterSelectionDialog = true;
          return;
        }

        this.printing = true;
        const slot = this.getSlotContent(slotId);
        if (!slot) return;

        // Gruppiere nach Orders
        const orderGroups = {};
        slot.articles.forEach(article => {
          if (!orderGroups[article.orderId]) {
            orderGroups[article.orderId] = [];
          }
          orderGroups[article.orderId].push(article);
        });

        // Verarbeite entweder eine spezifische Order oder die erste komplette Order
        const processOrder = async (orderId, articles) => {
          const orderDetails = this.orderArticles[orderId];
          if (!orderDetails) return false;


          let packlistItemId = (await PacklistService.findPacklistItem(orderId)).data.id;
          await PacklistService.trackStart(packlistItemId);
          await PacklistService.print(orderId, printer.url, false);
          await PacklistService.trackEnd(packlistItemId);

          for (const article of articles) {
            if (article.shelf?.hashcode) {
              await PacklistService.removeMultiCartArticle(article.shelf.hashcode);
            }
          }
          return true;
        };

        if (specificOrderId) {
          // Verarbeite nur die spezifische Order
          await processOrder(specificOrderId, orderGroups[specificOrderId]);
        } else {
          // Finde und verarbeite die erste komplette Order
          for (const [orderId, articles] of Object.entries(orderGroups)) {
            if (await processOrder(orderId, articles)) {
              break;
            }
          }
        }

        await this.loadData();
        if (this.detailDialog) {
          await this.showSlotDetails(slotId); // Aktualisiere die Detailansicht
        }
      } catch (error) {
        console.error('Fehler beim Drucken und Packen:', error);
      } finally {
        this.printing = false;
      }
    },

    isOrderComplete(products) {
      if (!products || products.length === 0) return false;

      // Filtere relevante Artikel (mit Attributen außer nur vQty)
      const relevantProducts = products.filter(article => this.shouldConsiderForShelfStatus(article));

      // Wenn keine relevanten Artikel vorhanden sind, ist die Bestellung "vollständig"
      if (relevantProducts.length === 0) return true;

      return relevantProducts.every(article => article.status === 'ready');
    },

    // Neue Methode zur Prüfung, ob ein Artikel für den Regalstatus berücksichtigt werden soll
    shouldConsiderForShelfStatus(article) {
      // Ignoriere Artikel ohne Attribute
      if (!article.attributes || Object.keys(article.attributes).length === 0) {
        return false;
      }

      // Ignoriere Artikel mit nur dem Attribut vQty
      if (Object.keys(article.attributes).length === 1 && 'vQty' in article.attributes) {
        return false;
      }

      // Alle anderen Artikel werden berücksichtigt
      return true;
    },

    openProblemDialog(orderId) {
      this.problemOrderId = orderId;
      this.showProblemDialog = true;
    },

    canReportProblem(order) {
      // Prüfe ob die Bestellung mindestens 24 Stunden alt ist
      const orderDetails = this.orderArticles[order.orderId];
      if (!orderDetails || !orderDetails.createDate) {
        // Fallback auf timestamp wenn createDate nicht vorhanden
        const orderAge = moment().diff(moment(order.timestamp), 'hours');
        return orderAge >= 24;
      }
      
      const orderAge = moment().diff(moment(orderDetails.createDate), 'hours');
      return orderAge >= 24;
    },

    closeProblemDialog() {
      this.showProblemDialog = false;
      this.selectedProblemType = null;
      this.problemDescription = '';
      this.problemOrderId = null;
    },

    async reportProblem() {
      if (!this.selectedProblemType || !this.problemDescription || !this.problemOrderId) {
        return;
      }

      // Zusätzliche Validierung für 24 Stunden
      const orderAge = this.getOrderAgeInHours(this.problemOrderId);
      if (orderAge < 24) {
        console.error('Bestellung ist noch keine 24 Stunden alt');
        return;
      }

      this.reportingProblem = true;
      try {
        const note = `${this.selectedProblemType}: ${this.problemDescription}`;
        
        // Sende Feedback und setze reported Flag
        await Promise.all([
          PacklistService.sendFeedback(this.problemOrderId, note),
          PacklistService.reportMultiCartOrder(this.problemOrderId, true)
        ]);
        
        // Problem erfolgreich gemeldet
        this.closeProblemDialog();

        // Aktualisiere die Daten
        await this.loadData();

        // Zeige Erfolgsmeldung (optional)
        console.log('Problem erfolgreich gemeldet');
      } catch (error) {
        console.error('Fehler beim Melden des Problems:', error);
      } finally {
        this.reportingProblem = false;
      }
    },

    async unreportProblem(orderId) {
      try {
        await PacklistService.unreportMultiCartOrder(orderId);

        // Aktualisiere die Daten
        await this.loadData();

        console.log(`Problem für Bestellung ${orderId} wurde gelöst`);
      } catch (error) {
        console.error('Fehler beim Lösen des Problems:', error);
      }
    },

    getOrderAgeInHours(orderId) {
      const orderDetails = this.orderArticles[orderId];
      if (!orderDetails || !orderDetails.createDate) {
        return 0;
      }
      return moment().diff(moment(orderDetails.createDate), 'hours');
    },

    getOrderAge(order) {
      const orderDetails = this.orderArticles[order.orderId];
      if (orderDetails && orderDetails.createDate) {
        return moment(orderDetails.createDate).fromNow();
      }
      // Fallback auf timestamp wenn kein createDate vorhanden
      if (order.timestamp) {
        return moment(order.timestamp).fromNow();
      }
      return 'Unbekannt';
    },

    openOrderFromList(order) {
      // Finde den Slot der Bestellung
      this.showSlotDetails(order.slot);
    },

    scrollToOrderSection(section) {
      // Could be used later if we add expandable sections
      console.log('Order section clicked:', section);
    },

    isSlotLocked(slot) {
      if (!slot) return false;
      if (!slot.lockedUntil) return false;

      // Check if lock is still valid
      const now = new Date();
      const lockedUntil = new Date(slot.lockedUntil);
      return lockedUntil > now;
    },

    hasSlotProblem(slot) {
      if (!slot || !slot.articles || slot.articles.length === 0) return false;

      // Check if any article in this slot has a problem
      return slot.articles.some(article => article.hasProblem === true);
    },

    getLockedInfo(slot) {
      if (!slot || !this.isSlotLocked(slot)) return '';

      const lockedBy = slot.lockedBy || 'Unbekannt';
      return `Gesperrt von ${lockedBy}`;
    },

    openOrderInWizard(orderId) {
      // Close detail dialog
      this.detailDialog = false;
      
      // Navigate to MulticartWizard with orderId as query parameter
      this.$router.push({
        path: '/multicart-wizard',
        query: { orderId }
      });
    },

    openSupportPage(orderId) {
      // Close detail dialog
      this.detailDialog = false;
      
      // Navigate to support/problem reporting page
      this.$router.push({
        path: '/support',
        query: { search: orderId }
      });
    },

    isOrderReadyToPack(order) {
      // Check if order has all products in shelf
      const orderDetails = this.orderArticles[order.orderId];
      if (!orderDetails || !orderDetails.products) {
        return false;
      }

      // Filter relevant products (with attributes except only vQty)
      const relevantProducts = orderDetails.products.filter(article => this.shouldConsiderForShelfStatus(article));
      
      // If no relevant products, order is "ready"
      if (relevantProducts.length === 0) {
        return true;
      }

      // Check if all relevant products are in shelf
      return relevantProducts.every(article => article.shelf);
    },

    getPackDisabledReason(order) {
      const orderDetails = this.orderArticles[order.orderId];
      if (!orderDetails || !orderDetails.products) {
        return 'Bestelldaten werden geladen...';
      }

      // Filter relevant products
      const relevantProducts = orderDetails.products.filter(article => this.shouldConsiderForShelfStatus(article));

      if (relevantProducts.length === 0) {
        return '';
      }

      const productsInShelf = relevantProducts.filter(article => article.shelf).length;
      const totalProducts = relevantProducts.length;
      const missingProducts = totalProducts - productsInShelf;

      if (missingProducts > 0) {
        return `Noch ${missingProducts} von ${totalProducts} Artikel${missingProducts > 1 ? 'n' : ''} fehlt im Regal`;
      }

      return '';
    },

    handleExtraSlotSuccess(message) {
      console.log('Extra slot success:', message);
      // Could show a notification/snackbar here if desired
    },

    handleExtraSlotError(message) {
      console.error('Extra slot error:', message);
      // Could show a notification/snackbar here if desired
    },
  },

  computed: {
    progressColor() {
      if (this.loadProgress < 30) return 'error';
      if (this.loadProgress < 70) return 'warning';
      return 'success';
    },

    allOrders() {
      const orders = [];
      
      Object.entries(this.slots).forEach(([slotId, slot]) => {
        if (!slot || !slot.articles || slot.articles.length === 0) return;

        // Gruppiere Artikel nach OrderId
        const orderGroups = {};
        slot.articles.forEach(article => {
          if (!orderGroups[article.orderId]) {
            orderGroups[article.orderId] = {
              orderId: article.orderId,
              slot: slotId,
              articles: [],
              timestamp: article.timestamp,
              createDate: this.orderArticles[article.orderId]?.createDate || article.timestamp,
              hasProblem: article.hasProblem || false
            };
          }
          orderGroups[article.orderId].articles.push(article);
          // Wenn irgendein Artikel ein Problem hat, markiere die gesamte Bestellung
          if (article.hasProblem) {
            orderGroups[article.orderId].hasProblem = true;
          }
        });

        // Füge die gruppierten Bestellungen zur Liste hinzu
        Object.values(orderGroups).forEach(orderGroup => {
          const orderDetails = this.orderArticles[orderGroup.orderId];
          if (orderDetails && orderDetails.products) {
            // Bestimme den Status der Bestellung
            const relevantProducts = orderDetails.products.filter(p => this.shouldConsiderForShelfStatus(p));
            
            let status = 'incomplete';
            if (relevantProducts.length === 0) {
              status = 'ready'; // Nur Zubehör
            } else {
              const allInShelf = relevantProducts.every(p => p.shelf);
              const allReady = relevantProducts.every(p => p.status === 'ready');
              
              if (allInShelf) {
                status = 'readyToPack'; // Alles im Regal (Grün)
              } else if (allReady) {
                status = 'ready'; // Alle bereit
              }
            }

            orders.push({
              ...orderGroup,
              status,
              orderDetails
            });
          }
        });
      });

      // Sortiere nach createDate (älteste zuerst)
      return orders.sort((a, b) => {
        const dateA = moment(a.createDate);
        const dateB = moment(b.createDate);
        return dateA.diff(dateB);
      });
    },

    readyOrders() {
      return this.allOrders.filter(order => order.status === 'ready');
    },

    readyToPackOrders() {
      return this.allOrders.filter(order => order.status === 'readyToPack');
    },

    incompleteOrders() {
      return this.allOrders.filter(order => order.status === 'incomplete');
    },
  },

  watch: {
    searchQuery(newQuery) {
      this.searchResults.clear();
      if (newQuery) {
        Object.entries(this.slots).forEach(([slotId, slot]) => {
          if (this.searchInSlot(slot, newQuery)) {
            this.searchResults.add(slotId);
          }
        });
      }
    }
  }
};
</script>

<style scoped>
/* Enhanced Header Styles (matching Packlists) */
.enhanced-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 0;
  margin-bottom: 32px;
}

.enhanced-header .v-container {
  padding-top: 24px;
  padding-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-title-section .page-title {
  font-size: 2.5rem;
  font-weight: 300;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions .action-button {
  min-width: 160px;
  height: 48px;
  font-weight: 500;
}

.controls-bar {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
}

.controls-flex {
  display: flex;
  align-items: center;
  gap: 20px;
}

.input-group {
  flex: 1;
  display: flex;
  align-items: center;
}

.search-field {
  background: white;
  border-radius: 8px;
}

.search-field >>> .v-input__control {
  min-height: 40px !important;
}

.search-field >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.shelf-toggle {
  background: white !important;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.shelf-toggle >>> .v-btn {
  height: 40px;
  color: rgba(0, 0, 0, 0.87) !important;
}

.shelf-toggle >>> .v-btn--active {
  background-color: #1976d2 !important;
  color: white !important;
}

.shelf-toggle >>> .v-btn--active:before {
  opacity: 0 !important;
}

/* Order Status Overview Badges */
.order-status-overview {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-left: auto;
}

.status-badge {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 100px;
}

.status-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.status-badge.status-ready {
  border-left: 4px solid #2196F3;
}

.status-badge.status-ready-to-pack {
  border-left: 4px solid #4CAF50;
}

.status-badge.status-incomplete {
  border-left: 4px solid #FF9800;
}

.status-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.status-count {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  line-height: 1;
}

.status-label {
  font-size: 11px;
  color: #666;
  line-height: 1;
}

/* Compact Shelf Grid Styles */
.shelf-container {
  margin-bottom: 0 !important;
}

.shelf-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 !important;
}

.shelf-grid-row {
  display: flex;
  align-items: stretch;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px;
  flex: 1;
  min-height: 0;
}

.shelf-column-header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  font-size: 22px;
  font-weight: bold;
  color: #2c3e50;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-right: 8px;
}

.shelf-slots-container {
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  gap: 4px;
  flex: 1;
}

.shelf-slot {
  position: relative;
  cursor: pointer;
  border-radius: 6px;
  padding: 8px 4px;
  min-height: 80px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.shelf-slot:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
}

.slot-lock-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(255, 152, 0, 0.95);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: lockPulse 2s infinite;
  z-index: 2;
}

.slot-problem-badge {
  position: absolute;
  top: 2px;
  left: 2px;
  background: rgba(244, 67, 54, 0.95);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: problemPulse 2s infinite;
  z-index: 2;
}

@keyframes lockPulse {
  0%, 100% {
    background: rgba(255, 152, 0, 0.95);
  }
  50% {
    background: rgba(255, 152, 0, 0.7);
  }
}

.slot-id {
  font-size: 14px;
  font-weight: bold;
  color: inherit;
  margin-bottom: 2px;
}

.slot-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  margin-top: 2px;
}

.slot-count {
  font-size: 12px;
  font-weight: bold;
  color: inherit;
}

.slot-age {
  font-size: 9px;
  color: inherit;
  opacity: 0.8;
}

/* Slot Status Colors */
.empty-slot {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  color: #999;
}

.active-slot {
  background-color: #e3f2fd;
  border: 2px solid #90caf9;
  color: #1976d2;
}

.warning-slot {
  background-color: #fff3e0;
  border: 2px solid #ffb74d;
  color: #f57c00;
}

.critical-slot {
  background-color: #ffebee;
  border: 2px solid #ef5350;
  color: #c62828;
  animation: criticalPulse 2s infinite;
}

@keyframes criticalPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 83, 80, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(239, 83, 80, 0);
  }
}

.complete-slot {
  background: linear-gradient(135deg, #4CAF50, #81c784);
  border: 2px solid #4CAF50;
  color: white;
  animation: completePulse 2s infinite;
}

@keyframes completePulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0);
  }
}

.problem-slot {
  background-color: #ffcdd2;
  border: 2px solid #f44336;
  color: #b71c1c;
  animation: problemPulse 2s infinite;
}

@keyframes problemPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(244, 67, 54, 0);
  }
}

/* Dialog Styles */
.detail-dialog {
  border-radius: 12px;
}

.headline {
  font-size: 24px !important;
  padding: 20px 24px !important;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

::v-deep .v-expansion-panel {
  margin-bottom: 8px;
  border-radius: 8px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

::v-deep .v-expansion-panel-header {
  font-size: 16px;
  padding: 16px 24px;
  background: #f8f9fa;
}

::v-deep .v-expansion-panel-content {
  font-size: 14px;
  padding: 20px;
}

::v-deep .v-data-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::v-deep .v-data-table th {
  font-size: 15px;
  font-weight: bold;
  background: #f8f9fa !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.attributes-list {
  font-size: 1.25rem;
  color: rgba(0, 0, 0, 0.87);
}

.attribute-item {
  margin-bottom: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

/* Search Result Highlighting */
.search-result-slot {
  background: linear-gradient(135deg, #9c27b0, #ba68c8) !important;
  border: 2px solid #9c27b0 !important;
  color: white !important;
  animation: searchPulse 2s infinite;
}

@keyframes searchPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(156, 39, 176, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(156, 39, 176, 0);
  }
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Styles für die drei-Spalten-Ansicht */
.order-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  margin-bottom: 4px;
}

.order-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.order-list-item .v-list-item__content {
  padding: 8px 0;
}

.order-list-item .v-list-item__title {
  font-weight: 500;
}

.order-list-item .v-list-item__subtitle {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  font-size: 0.875rem;
}

/* Page Layout */
.v-container.container--fluid {
  height: calc(100vh - 320px);
  display: flex;
  flex-direction: column;
}

.v-container.container--fluid > .v-row {
  flex: 1;
  margin: 0 !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
  }
  
  .header-title-section .page-title {
    font-size: 2rem;
  }
  
  .controls-flex {
    flex-direction: column;
    gap: 16px;
  }
  
  .input-group {
    width: 100%;
  }
  
  .order-status-overview {
    width: 100%;
    justify-content: center;
  }
  
  .shelf-column-header {
    min-width: 50px;
    font-size: 18px;
  }
  
  .shelf-slots-container {
    grid-template-columns: repeat(8, 1fr);
  }
  
  .slot-id {
    font-size: 12px;
  }
  
  .slot-count {
    font-size: 11px;
  }
  
  .shelf-slot {
    min-height: 60px;
  }
}

@media (max-width: 600px) {
  .enhanced-header {
    margin-bottom: 20px;
  }
  
  .enhanced-header .v-container {
    padding-top: 16px;
    padding-bottom: 16px;
  }
  
  .header-title-section .page-title {
    font-size: 1.75rem;
  }
  
  .controls-bar {
    padding: 16px;
  }
  
  .order-status-overview {
    flex-direction: column;
    width: 100%;
  }
  
  .status-badge {
    width: 100%;
    justify-content: center;
  }
  
  .shelf-column-header {
    min-width: 40px;
    font-size: 16px;
  }
  
  .shelf-slots-container {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .slot-id {
    font-size: 11px;
  }
  
  .slot-count {
    font-size: 10px;
  }
  
  .shelf-slot {
    min-height: 50px;
  }
  
  .v-container.container--fluid {
    height: auto;
  }
}

/* Detail Dialog Styles */
.dialog-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 20px 24px;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.dialog-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.order-card {
  border-radius: 12px !important;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.order-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
}

.order-card-problem {
  border-color: #f44336;
}

.order-card-old {
  border-color: #ff9800;
}

.order-loading {
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
}

.order-card-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.order-id {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.order-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
}

.info-icon {
  color: #1976d2;
  margin-top: 2px;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.order-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.order-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

@media (max-width: 600px) {
  .orders-grid {
    grid-template-columns: 1fr;
  }
  
  .order-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
